import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { uploadCenterMedia, listMyCenterMedia, deleteCenterMedia } from '../../services/centerMedia'
import {
  PhotoIcon,
  VideoCameraIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  TicketIcon,
  CalendarIcon,
  TagIcon,
  ShoppingBagIcon,
  WalletIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  ArrowsPointingOutIcon,
  PlayIcon
} from '@heroicons/react/24/outline'

const CenterMediaPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [media, setMedia] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [filter, setFilter] = useState('ALL')
  const [selectedFile, setSelectedFile] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [caption, setCaption] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [showMediaModal, setShowMediaModal] = useState(false)

  const displayName = user?.organization || user?.name || 'Event Center'

  useEffect(() => {
    const u = getUser()
    setUser(u)
  }, [])

  useEffect(() => {
    fetchMedia()
  }, [filter])

  const fetchMedia = async () => {
    setLoading(true)
    setError('')
    try {
      const typeFilter = filter === 'ALL' ? '' : filter
      const res = await listMyCenterMedia(typeFilter)
      if (res.status === 200) {
        // Ensure we have an array and log for debugging
        const mediaData = Array.isArray(res.data) ? res.data : []
        console.log('Fetched media:', mediaData.length, 'items')
        setMedia(mediaData)
      } else {
        setError(res.error || 'Failed to load media')
        setMedia([])
      }
    } catch (err) {
      console.error('Error fetching media:', err)
      setError('Failed to load media. Please try again.')
      setMedia([])
    }
    setLoading(false)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')

    if (!isImage && !isVideo) {
      setError('Please select an image or video file')
      return
    }

    // Validate file size
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024 // 50MB for video, 10MB for image
    if (file.size > maxSize) {
      setError(`File size exceeds maximum limit of ${maxSize / (1024 * 1024)}MB`)
      return
    }

    setSelectedFile(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setFilePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file')
      return
    }

    setUploading(true)
    setError('')

    const res = await uploadCenterMedia(selectedFile, caption)
    
    if (res.status === 201 || res.status === 200) {
      setSuccess('Media uploaded successfully')
      setShowUploadModal(false)
      setSelectedFile(null)
      setFilePreview(null)
      setCaption('')
      fetchMedia()
      setTimeout(() => setSuccess(''), 3000)
    } else {
      setError(res.error || 'Failed to upload media')
    }

    setUploading(false)
  }

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media? This action cannot be undone.')) {
      return
    }

    const res = await deleteCenterMedia(mediaId)
    
    if (res.status === 200) {
      setSuccess('Media deleted successfully')
      setMedia(prev => prev.filter(m => m._id !== mediaId))
      setTimeout(() => setSuccess(''), 3000)
    } else {
      setError(res.error || 'Failed to delete media')
    }
  }

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setSelectedFile(null)
    setFilePreview(null)
    setCaption('')
    setError('')
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: HomeIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center/profile' }
  ]

  const filteredMedia = filter === 'ALL' ? media : media.filter(m => m.type === filter)

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
                <TicketIcon className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Obule</span>
            </Link>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = window.location.pathname === tab.path
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/auth/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Media Gallery</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 mb-2">Media Gallery</h1>
                <p className="text-slate-600">Upload and manage your images and videos</p>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/30"
              >
                <PlusIcon className="w-5 h-5" />
                Upload Media
              </button>
            </div>

            {/* Notifications */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 flex items-center gap-3">
                <XCircleIcon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{error}</span>
                <button onClick={() => setError('')} className="ml-auto">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-100 text-green-700 flex items-center gap-3">
                <CheckCircleIcon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{success}</span>
                <button onClick={() => setSuccess('')} className="ml-auto">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { id: 'ALL', label: 'All Media', count: media.length },
                { id: 'IMAGE', label: 'Images', count: media.filter(m => m.type === 'IMAGE').length },
                { id: 'VIDEO', label: 'Videos', count: media.filter(m => m.type === 'VIDEO').length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setFilter(tab.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    filter === tab.id
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                      : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                  }`}
                >
                  {tab.label} <span className="ml-2 opacity-75">({tab.count})</span>
                </button>
              ))}
            </div>

            {/* Media Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                  <p className="mt-4 text-slate-600">Loading media...</p>
                </div>
              </div>
            ) : filteredMedia.length === 0 ? (
              <div className="text-center py-20">
                <PhotoIcon className="mx-auto h-24 w-24 text-slate-300" />
                <h3 className="mt-6 text-2xl font-semibold text-slate-900">No media found</h3>
                <p className="mt-2 text-slate-600 mb-6">
                  {filter === 'ALL' 
                    ? "Upload your first image or video to showcase your venue."
                    : `No ${filter.toLowerCase()}s uploaded yet.`}
                </p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
                >
                  <PlusIcon className="w-5 h-5" />
                  Upload Media
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredMedia.map(item => (
                  <div key={item._id} className="group relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-xl transition-all">
                    {/* Media Display */}
                    <div className="aspect-square bg-slate-100 relative overflow-hidden cursor-pointer" onClick={() => { setSelectedMedia(item); setShowMediaModal(true); }}>
                      {item.type === 'IMAGE' ? (
                        <img 
                          src={item.url} 
                          alt={item.caption || 'Media'} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            console.error('Failed to load image:', item.url, item)
                            e.target.style.display = 'none'
                            e.target.parentElement.innerHTML = `
                              <div class="w-full h-full flex items-center justify-center text-red-500">
                                <p class="text-xs">Failed to load image</p>
                              </div>
                            `
                          }}
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <video 
                            src={item.url} 
                            className="w-full h-full object-cover"
                            controls={false}
                            onError={(e) => {
                              console.error('Failed to load video:', item.url, item)
                              e.target.style.display = 'none'
                            }}
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <PlayIcon className="w-16 h-16 text-white" />
                          </div>
                        </div>
                      )}
                      
                      {/* Type Badge */}
                      <div className="absolute top-3 left-3 z-10">
                        <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase ${
                          item.type === 'IMAGE' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-purple-500 text-white'
                        }`}>
                          {item.type}
                        </span>
                      </div>

                      {/* Expand/View Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedMedia(item)
                          setShowMediaModal(true)
                        }}
                        className="absolute bottom-3 right-3 w-10 h-10 bg-white/90 hover:bg-white text-slate-700 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg z-10"
                        title={item.type === 'IMAGE' ? 'Expand image' : 'Play video'}
                      >
                        {item.type === 'IMAGE' ? (
                          <ArrowsPointingOutIcon className="w-5 h-5" />
                        ) : (
                          <PlayIcon className="w-5 h-5" />
                        )}
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(item._id)
                        }}
                        className="absolute top-3 right-3 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg z-10"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Caption */}
                    {item.caption && (
                      <div className="p-4 border-t border-slate-100">
                        <p className="text-sm text-slate-600 line-clamp-2">{item.caption}</p>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="px-4 pb-4">
                      <p className="text-xs text-slate-400">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Media Viewer Modal */}
            {showMediaModal && selectedMedia && (
              <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setShowMediaModal(false); setSelectedMedia(null); }}>
                <div className="relative max-w-7xl w-full h-full max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  {/* Close Button */}
                  <button
                    onClick={() => { setShowMediaModal(false); setSelectedMedia(null); }}
                    className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all z-50 backdrop-blur-sm"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>

                  {/* Media Content */}
                  <div className="w-full h-full flex items-center justify-center">
                    {selectedMedia.type === 'IMAGE' ? (
                      <img 
                        src={selectedMedia.url} 
                        alt={selectedMedia.caption || 'Media'} 
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        onError={(e) => {
                          console.error('Failed to load image in modal:', selectedMedia.url)
                          e.target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="w-full max-w-5xl">
                        <video 
                          src={selectedMedia.url} 
                          className="w-full h-full max-h-[80vh] rounded-lg shadow-2xl"
                          controls
                          autoPlay
                          onError={(e) => {
                            console.error('Failed to load video in modal:', selectedMedia.url)
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Caption */}
                  {selectedMedia.caption && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-6 py-3 rounded-lg max-w-2xl text-center">
                      <p className="text-sm">{selectedMedia.caption}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-slate-900">Upload Media</h3>
                    <button onClick={closeUploadModal} className="text-slate-400 hover:text-slate-900">
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  {/* File Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Select Image or Video
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="media-upload"
                      />
                      <label
                        htmlFor="media-upload"
                        className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-all"
                      >
                        {filePreview ? (
                          <div className="relative w-full h-full">
                            {selectedFile?.type.startsWith('image/') ? (
                              <img src={filePreview} alt="Preview" className="w-full h-full object-contain" />
                            ) : (
                              <video src={filePreview} className="w-full h-full object-contain" controls />
                            )}
                          </div>
                        ) : (
                          <div className="text-center">
                            <PhotoIcon className="mx-auto h-12 w-12 text-slate-400" />
                            <p className="mt-2 text-sm text-slate-600">Click to select file</p>
                            <p className="text-xs text-slate-400 mt-1">Images (up to 10MB) or Videos (up to 50MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                    {selectedFile && (
                      <p className="mt-2 text-sm text-slate-600">
                        Selected: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </p>
                    )}
                  </div>

                  {/* Caption Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Caption (Optional)
                    </label>
                    <textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      rows={3}
                      placeholder="Add a caption to describe this media..."
                      className="w-full rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={closeUploadModal}
                      className="flex-1 py-3 border-2 border-slate-300 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpload}
                      disabled={!selectedFile || uploading}
                      className={`flex-1 py-3 rounded-lg font-semibold transition shadow-lg ${
                        !selectedFile || uploading
                          ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                      }`}
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default CenterMediaPage

